/**
 * Menu mobile CDS : tiroir basé sur <dialog>, ouvert avec showModal().
 * Remplace <header-drawer>, dont les liens restaient parfois invisibles sur iPhone.
 * Le dialog modal est affiché dans le top layer : il ne dépend ni du header (sticky, opacité)
 * ni des animations de la page, et le navigateur gère nativement le focus et la touche Échap.
 */
class CdsMobileMenu extends HTMLElement {
  #controller = new AbortController();

  /** @type {(() => void) | null} */
  #cancelPendingClose = null;

  connectedCallback() {
    const { signal } = this.#controller;
    const { dialog, toggleButton } = this;

    if (!dialog || !toggleButton) return;

    toggleButton.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-cds-menu-close]')?.addEventListener('click', () => this.close(), { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });

    // Escape: replace the native instant close to keep the slide-out animation
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );

    // Back/forward cache: the page can come back with the menu still open
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.close({ immediate: true });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    // Only release the lock this menu owns (the cart drawer uses the same attribute)
    if (this.dialog?.open) document.documentElement.removeAttribute('scroll-lock');
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {HTMLButtonElement | null} */
  get toggleButton() {
    return this.querySelector('[data-cds-menu-open]');
  }

  open() {
    const { dialog, toggleButton } = this;
    if (!dialog) return;

    this.#cancelPendingClose?.();
    if (!dialog.open) dialog.showModal();

    document.documentElement.setAttribute('scroll-lock', '');
    toggleButton?.setAttribute('aria-expanded', 'true');

    // Let the browser paint the off-screen position before starting the slide-in transition
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  }

  /**
   * @param {{ immediate?: boolean }} [options]
   */
  close({ immediate = false } = {}) {
    const { dialog, toggleButton } = this;
    if (!dialog?.open) return;

    this.#cancelPendingClose?.();
    dialog.classList.remove('is-open');
    toggleButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');

    const finish = () => {
      this.#cancelPendingClose?.();
      dialog.close();
    };

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    /** @param {TransitionEvent} event */
    const onTransitionEnd = (event) => {
      if (event.target === dialog && event.propertyName === 'transform') finish();
    };
    // Safety net: transitionend never fires if the tab is hidden mid-animation
    const timeout = setTimeout(finish, 500);

    dialog.addEventListener('transitionend', onTransitionEnd);
    this.#cancelPendingClose = () => {
      clearTimeout(timeout);
      dialog.removeEventListener('transitionend', onTransitionEnd);
      this.#cancelPendingClose = null;
    };
  }

  /**
   * Clicks on the ::backdrop target the dialog itself, outside of its visible box.
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    const { dialog } = this;
    if (!dialog || event.target !== dialog) return;

    const rect = dialog.getBoundingClientRect();
    const isInside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isInside) this.close();
  };
}

if (!customElements.get('cds-mobile-menu')) {
  customElements.define('cds-mobile-menu', CdsMobileMenu);
}
